// ── Elevation × slope rules, extracted VERBATIM from the old Cognito DD form ─────────────────────
// Source: https://www.cognitoforms.com/SamuiLawFirmCoLtd/DueDiligence, form-def model, 2026-08-13.
// The owner's instruction is exact parity ("新系统做DD需要和老系统一样"), so the texts below are the
// old form's own wording — including its quirk of "they are:" in lower case. Do not polish them
// without the owner's say-so: these sentences have been going out on issued reports for years and
// the firm's lawyers know them by heart. Four changes so far: the spelling, which the reporter asked
// for in report 9e17f7ac ("buidling" became "building" and the stray spaces before a comma or full
// stop went); the slope's unit, degrees to percent (report 38068b80): the form's Slope answers are
// in percent now, so "greater than 50 degrees" became "greater than 50%" in the three bands above 35;
// the 480 m² line of Zone 2 and Zone 3.2 above 35%, which the reporter reworded in report
// ed6bb510 (5th opening) to say it applies only to land that is to be subdivided; and Zone 2 at a
// slope of 35–50%, whose lines a)–f) report 9f0e79b9 (2nd opening) replaced with the reporter's own
// wording, split into three lettered lines as the reporter approved, each square wah figure also given
// in m2 (square wah × 4), and no line for a plot of exactly 100 square wah ("not 100% clear yet").
// Zone 2 above 50% keeps a)–f), so its line that construction is prohibited shows only at that answer.
// Every other word is still the old form's.
//
// The old form held SIX full summary variants (_p12.._p17), one per elevation×slope pair:
//   Zone 2 (0-80m)      × <35%  → no additional restrictions
//   Zone 2 (0-80m)      × >35%  → conditions a)–f)
//   Zone 3.1 (80-140m)  × <35%  → "may apply" a)–e)   (connector "but,")
//   Zone 3.1 (80-140m)  × >35%  → "may apply" a)–i)   (connector "and,")
//   Zone 3.2 (>140m)    × <35%  → conditions a)–h)
//   Zone 3.2 (>140m)    × >35%  → conditions a)–i)
// Zone 1 (sea level) had NO variant: the old form showed no elevation paragraph at all, and the
// owner chose to keep that behaviour.
// Its ">35%" is two Slope answers now, 35–50% and above 50% (slopeKey 'gt35' / 'gt50'), each with a
// row of its own. Above 50% every band prints the old form's ">35%" lines; at 35–50% Zone 3.1 and 3.2
// print them too until the reporter sends their wording, and Zone 2 prints report 9f0e79b9's three.

export type ElevKey = '0-80' | '80-140' | '140+' | '';
export type SlopeKey = 'lt35' | 'gt35' | 'gt50' | '';

export const elevationKey = (v: any): ElevKey => {
  const s = String(v || '');
  if (s.includes('between 0-80m')) return '0-80';
  if (s.includes('between 80-140m')) return '80-140';
  if (s.includes('greater than 140')) return '140+';
  return '';                                    // Zone 1 (sea level), or nothing chosen yet
};
// The elevation as the CONSIDERATION note words it (report b9d3f0f3): the answer 'Zone 2 (between 0-80m
// above sea level)' reads 'zone 2 (between 0 and 80 m above sea level)'. Only the note's sentence changes:
// the saved answer, the form's list and the Property Summary keep the form's words.
export const elevationInSentence = (v: any): string =>
  String(v || '').replace(/^Zone/, 'zone').replace(/(\d+)-(\d+)/, '$1 and $2').replace(/(\d)m\b/, '$1 m');
// The Property page's Land Elevation upload is required when the elevation names 140m, which only Zone
// 3.1 (between 80-140m) and Zone 3.2 (greater than 140m) do (report ed6bb510's later opening: "make the
// elevation upload required when the elevation is zone 3.1 and 3.2"). The old form's "Land Elevation
// (Estimate)" box was shown, and required, on this same test; the box itself now always shows.
export const needsElevationEstimate = (v: any): boolean => String(v || '').includes('140m');
// The two answers above 35% read a band each: 'greater than 35% but, less than 50%' is 'gt35' and
// 'greater than 50%' is 'gt50'. They shared the band above 35 (report 38068b80) until report 9f0e79b9
// reworded Zone 2 at 35–50% alone, and asked that the line barring construction above 50% "appear if
// above 50% is selected". The old form's answers in degrees read as before: 'greater than 35 degrees'
// is the 35–50% one, as nothing saved says whether the land was over 50.
export const slopeKey = (v: any): SlopeKey => {
  const s = String(v || '');
  if (s.includes('less than 35')) return 'lt35';
  if (s.includes('greater than 50')) return 'gt50';
  if (s.includes('greater than 35')) return 'gt35';
  return '';
};

export type ElevationVariant = {
  elev: ElevKey; slope: SlopeKey;
  /** the word joining the two bolded selections: "and" / "but," / "and," — old form varies it */
  connector: string;
  /** sentence after the selections; conditions (if any) follow as an indented a)-i) list */
  intro: string;
  conditions: string[];
};

const MAY_APPLY = 'there are some additional restrictions that may apply to the zoning, maximum usage and/or suitability of the land. they are:';
const THESE_ARE = 'there are some additional restrictions to the standard zoning regulations, maximum usage and/or suitability of the land. These conditions are:';

export const ELEVATION_VARIANTS: ElevationVariant[] = [
  {
    elev: '0-80', slope: 'lt35', connector: 'and',
    intro: 'there should not be any additional restrictions to the standard zoning regulations, maximum usage and/or suitability of the land.',
    conditions: [],
  },
  // Report 9f0e79b9, Zone 2 at 35–50%. None of these lines is a "minimum plot size of N m2", "no more than N%
  // of the land can be built upon" or "at least N% of the land vacant" line, so plotConflicts and usageCap
  // find nothing here: no IMPORTANT NOTE and no lowered Maximum Usage, as the reporter asked ("remove all
  // other information, including the pop-up Important note for the time being").
  // b) and c) each hold for one side of 100 square wah, and the Property Summary greys the one the plot is
  // not on (linesNotForPlot, report 95fecc9a), read from their "measuring more/less than … (400 m2)".
  {
    elev: '0-80', slope: 'gt35', connector: 'and', intro: THESE_ARE,
    conditions: [
      'only detached houses or standalone buildings with a height of no more than 6 metres;',
      'for an approved land plot measuring more than 100 square wah (400 m2): the building footprint per building must not exceed 90 square metres, open space must comprise at least 60% of the land, green space must comprise at least 50% of the area;',
      'for an approved land plot measuring less than 100 square wah (400 m2): the building footprint per building must not exceed 70 square metres, at least 50% of the land must be permeable open space through which water can seep, green space must comprise at least 50% of the area, with perennial trees as the main component.',
    ],
  },
  {
    elev: '0-80', slope: 'gt50', connector: 'and', intro: THESE_ARE,
    conditions: [
      'a single dwelling only is permitted and;',
      'building cannot exceed 6m in height and;',
      'no more than 25% of the land can be built upon and;',
      '50% of land must contain native plants/trees and greenery and;',
      'a minimum plot size of 480 m² is required only if the land is to be subdivided, and;',
      'if the slope is greater than 50% then, construction on the plot is prohibited.',
    ],
  },
  {
    elev: '80-140', slope: 'lt35', connector: 'but,', intro: MAY_APPLY,
    conditions: [
      'construction cannot exceed 6m in total height and;',
      'must leave at least 50% of the land vacant or whichever is lower - 50% or the "Maximum Usage" stated above ({MaximumUsage}) and;',
      'must include an approved water drainage system for the prevention of flooding and;',
      'must have a roof consisting of 80% of the total building area, and roof color must match natural surroundings and;',
      'a minimum plot size of 400m2, otherwise construction is prohibited.',
    ],
  },
  {
    elev: '80-140', slope: 'gt35', connector: 'and,', intro: MAY_APPLY,
    conditions: [
      'a single dwelling only is permitted and;',
      'building cannot exceed 6m in height and;',
      'construction cannot exceed 80m2 in total enclosed area and;',
      'no more than 25% of the land can be built upon and;',
      '50% of land must contain native plants/trees and greenery and;',
      'a minimum plot size of 480m2 is required otherwise construction is prohibited and;',
      'must include an approved water drainage system for the prevention of flooding and;',
      'must have a roof consisting of 80% of the total building area, and roof color must match natural surroundings and;',
      'if the slope of the land is greater than 50% then, construction on the plot is prohibited.',
    ],
  },
  {
    elev: '80-140', slope: 'gt50', connector: 'and,', intro: MAY_APPLY,
    conditions: [
      'a single dwelling only is permitted and;',
      'building cannot exceed 6m in height and;',
      'construction cannot exceed 80m2 in total enclosed area and;',
      'no more than 25% of the land can be built upon and;',
      '50% of land must contain native plants/trees and greenery and;',
      'a minimum plot size of 480m2 is required otherwise construction is prohibited and;',
      'must include an approved water drainage system for the prevention of flooding and;',
      'must have a roof consisting of 80% of the total building area, and roof color must match natural surroundings and;',
      'if the slope of the land is greater than 50% then, construction on the plot is prohibited.',
    ],
  },
  {
    elev: '140+', slope: 'lt35', connector: 'and', intro: THESE_ARE,
    conditions: [
      'a single dwelling only is permitted and;',
      'building cannot exceed 6m in height and;',
      'construction cannot exceed 90m2 in total enclosed area and;',
      'no more than 40% of the land can be built upon and;',
      '50% of land must contain native plants/trees and greenery and;',
      'a minimum plot size of 400m2 is required and;',
      'must include an approved water drainage system for the prevention of flooding and;',
      'must have a roof consisting of 80% of the total building area, and roof color must match natural surroundings.',
    ],
  },
  {
    elev: '140+', slope: 'gt35', connector: 'and', intro: THESE_ARE,
    conditions: [
      'a single dwelling only is permitted and;',
      'building cannot exceed 6m in height and;',
      'construction cannot exceed 80m2 in total enclosed area and;',
      'no more than 25% of the land can be built upon and;',
      '50% of land must contain native plants/trees and greenery and;',
      'a minimum plot size of 480 m² is required only if the land is to be subdivided, and;',
      'must include an approved water drainage system for the prevention of flooding and;',
      'must have a roof consisting of 80% of the total building area, and roof color must match natural surroundings and;',
      'if the slope is greater than 50% then, construction on the plot is prohibited.',
    ],
  },
  {
    elev: '140+', slope: 'gt50', connector: 'and', intro: THESE_ARE,
    conditions: [
      'a single dwelling only is permitted and;',
      'building cannot exceed 6m in height and;',
      'construction cannot exceed 80m2 in total enclosed area and;',
      'no more than 25% of the land can be built upon and;',
      '50% of land must contain native plants/trees and greenery and;',
      'a minimum plot size of 480 m² is required only if the land is to be subdivided, and;',
      'must include an approved water drainage system for the prevention of flooding and;',
      'must have a roof consisting of 80% of the total building area, and roof color must match natural surroundings and;',
      'if the slope is greater than 50% then, construction on the plot is prohibited.',
    ],
  },
];

export function elevationVariant(landElevation: any, slope: any): ElevationVariant | null {
  const e = elevationKey(landElevation), s = slopeKey(slope);
  if (!e || !s) return null;
  return ELEVATION_VARIANTS.find(v => v.elev === e && v.slope === s) || null;
}

/**
 * Where the land breaks the selected band's own conditions: the IMPORTANT NOTE above the Property
 * Summary, and the condition lines it colours (report 9e17f7ac: "The important note box needs to
 * appear when the regulations below contradict the number that is put in the client's land or the
 * overall land size. Regardless of whether the elevation is 0, 80 or 140+"). The note used to be the
 * old form's _p10/_p11, which fired on the two high-elevation bands only, so Zone 2 above 35% never
 * flagged a 180m2 plot against its "minimum plot size of 480m2", nor a 50% Maximum Usage against its
 * "no more than 25% of the land can be built upon".
 * Every figure comes from the variant's own condition lines, so a band checks exactly what it prints:
 * "a minimum plot size of Nm2" against each plot size handed in (the land's, and the buyer's plot when
 * that is not the same size), and the coverage cap — "no more than N% of the land can be built upon",
 * or "at least N% of the land vacant", which leaves 100 − N — against the Maximum Usage. A band with
 * no such line (Zone 1, Zone 2 below 50%) has nothing to break. `cond` is the line's index (0 = a)).
 * Plot sizes come first, in the order handed in, then the coverage: the order the note prints them.
 */
export type PlotConflict =
  | { kind: 'size'; cond: number; min: number; size: number }
  | { kind: 'usage'; cond: number; max: number; usage: number };

// "m2" in the old form's lines, "m²" in the 480 m² line the reporter reworded (report ed6bb510).
const MIN_PLOT = /minimum plot size of (\d+(?:\.\d+)?)\s*m(?:2|²)/i;
const BUILT_MAX = /no more than (\d+(?:\.\d+)?)% of the land can be built upon/i;
const VACANT_MIN = /at least (\d+(?:\.\d+)?)% of the land vacant/i;

// The band's coverage line and the cap it sets (the lower one, were a band ever to print two).
function capLine(landElevation: any, slope: any): { cond: number; max: number } | null {
  const v = elevationVariant(landElevation, slope);
  if (!v) return null;
  let best: { cond: number; max: number } | null = null;
  for (let cond = 0; cond < v.conditions.length; cond++) {
    const built = BUILT_MAX.exec(v.conditions[cond]), vacant = VACANT_MIN.exec(v.conditions[cond]);
    const max = built ? Number(built[1]) : vacant ? 100 - Number(vacant[1]) : 0;
    if (max > 0 && (!best || max < best.max)) best = { cond, max };
  }
  return best;
}

/**
 * The most of the land the selected band lets be built upon, in %, read from its own lines: 25 for
 * Zone 3.1 / 3.2 above 35% and Zone 2 above 50%, 40 for Zone 3.2 below 35%, 50 for Zone 3.1 below 35%,
 * null for a band with no such line (Zone 1, Zone 2 below 50%). Report 73cc8058: the cap is a legal
 * limit, so the form lowers the Maximum Usage to it instead of only setting the line in red.
 * plotConflicts reads the same line, so the figure the form fills and the line the summary flags
 * cannot drift apart.
 */
export function usageCap(landElevation: any, slope: any): number | null {
  const line = capLine(landElevation, slope);
  return line ? line.max : null;
}

export function plotConflicts(landElevation: any, slope: any, sizes: any[], maximumUsage: any): PlotConflict[] {
  const v = elevationVariant(landElevation, slope);
  if (!v) return [];
  const all = sizes.map(s => Number(s) || 0);
  const plots = all.filter((s, i) => s > 0 && all.indexOf(s) === i);
  const usage = parseFloat(String(maximumUsage ?? '')) || 0;
  const out: PlotConflict[] = [];
  v.conditions.forEach((c, cond) => {
    const min = MIN_PLOT.exec(c);
    if (min) for (const size of plots) if (size < Number(min[1])) out.push({ kind: 'size', cond, min: Number(min[1]), size });
  });
  const cap = capLine(landElevation, slope);
  if (cap && usage > cap.max) out.push({ kind: 'usage', cond: cap.cond, max: cap.max, usage });
  return out;
}

/**
 * The lines written for a plot of another size, which the Property Summary greys (report 95fecc9a:
 * "highlight the applicable restriction, either B or C, by graying out the one that is not applicable").
 * A line that holds only for a plot "measuring more than / less than N square wah (M m2)" — Zone 2 at
 * 35–50%, its b) and c) — does not apply to a plot on the other side of M. `size` is the plot in m²; with
 * none every line stays, and at exactly M so do both, as neither line words that plot (report 9f0e79b9:
 * "not 100% clear yet"). The indexes count as plotConflicts' `cond` does (0 = a)).
 */
const PLOT_BAND = /measuring (more|less) than \d+(?:\.\d+)? square wah \((\d+(?:\.\d+)?)\s*m(?:2|²)\)/i;

export function linesNotForPlot(landElevation: any, slope: any, size: any): number[] {
  const v = elevationVariant(landElevation, slope);
  const plot = Number(size) || 0;
  if (!v || plot <= 0) return [];
  const out: number[] = [];
  v.conditions.forEach((c, cond) => {
    const band = PLOT_BAND.exec(c);
    if (band && (band[1].toLowerCase() === 'more' ? plot < Number(band[2]) : plot > Number(band[2]))) out.push(cond);
  });
  return out;
}
